import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useReveal, useParallax } from "@/hooks/useReveal";
import { PageHero, Eyebrow, SectionTitle, GoldButton, ArrowRight } from "@/components/shared";
import { Ornament } from "@/components/Ornament";
import { IMAGES, cafeGallery } from "@/data/treasures";

const CafePage = () => {
  const { t, lang } = useLang();
  const ref = useReveal([lang]);
  const parallaxRef = useParallax(60);

  const soonLabel = { de: "Preise bald verfügbar", en: "Prices coming soon", it: "Prezzi presto disponibili", fr: "Prix bientôt disponibles", pt: "Preços em breve" }[lang] || "Preise bald verfügbar";

  return (
    <div ref={ref}>
      <PageHero image={IMAGES.cafeInterior} eyebrow={t.cafe.eyebrow} title={t.cafe.title} lead={t.cafe.lead} />

      {/* Intro split */}
      <section className="py-24 md:py-32 bg-cream grain-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative" data-reveal>
            <div className="overflow-hidden rounded-2xl">
              <img ref={parallaxRef} src={cafeGallery[0]} alt="Café" className="w-full h-[520px] object-cover scale-110" loading="lazy" />
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-2xl border border-gold/40 hidden md:block" />
          </div>
          <div data-reveal data-reveal-delay="0.15">
            <Eyebrow>{t.cafe.eyebrow}</Eyebrow>
            <SectionTitle>{t.cafe.title}</SectionTitle>
            <p className="mt-6 text-espresso/70 text-lg leading-relaxed font-sans">{t.cafe.body1}</p>
            <p className="mt-4 text-espresso/70 text-lg leading-relaxed font-sans">{t.cafe.body2}</p>
            <Ornament className="mt-8 justify-start opacity-70" width={200} />
          </div>
        </div>
      </section>

      {/* Menu */}
      <section className="py-20 md:py-28 bg-espresso-deep grain-overlay">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14" data-reveal>
            <SectionTitle dark>{t.cafe.menuTitle}</SectionTitle>
            <Ornament className="mt-6 opacity-70" width={200} />
          </div>
          <div className="space-y-2">
            {t.cafe.menu.map((m, i) => (
              <div key={i} data-reveal data-reveal-delay={0.06 * i} data-testid={`menu-item-${i}`}
                className="flex items-end gap-4 py-5 border-b border-gold/15 group">
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-cream group-hover:text-gold transition-colors">{m.n}</h3>
                  <p className="text-cream/55 text-sm font-sans mt-1">{m.d}</p>
                </div>
                <span className="border-b border-dotted border-gold/30 flex-1 mb-2 hidden sm:block" />
                <span className="shrink-0 inline-flex items-center rounded-full border border-gold/40 px-3 py-1 text-gold/90 font-sans text-xs tracking-wide" data-testid={`menu-price-soon-${i}`}>{soonLabel}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 md:py-28 bg-sand">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid sm:grid-cols-3 gap-5">
            {cafeGallery.map((src, i) => (
              <div key={i} data-reveal data-reveal-delay={0.08 * i} className="overflow-hidden rounded-2xl group">
                <img src={src} alt="Café" className={`w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110 ${i === 1 ? "h-80 sm:h-96" : "h-80"}`} loading="lazy" />
              </div>
            ))}
          </div>
          <div className="text-center mt-14" data-reveal>
            <GoldButton as={Link} to="/kontakt" data-testid="cafe-visit-cta">{t.cta.visit} <ArrowRight className="w-4 h-4" /></GoldButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CafePage;
